import { useState, useEffect } from 'react';

interface OfflineData {
  jobs: any[];
  messages: any[];
  notifications: any[];
  lastSync: Date | null;
}

interface PendingAction {
  id: string;
  type: string;
  data: any;
  timestamp: string;
  retryCount?: number;
  maxRetries?: number;
}

export function useOffline() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineData, setOfflineData] = useState<OfflineData>({
    jobs: [],
    messages: [],
    notifications: [],
    lastSync: null
  });
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [syncInProgress, setSyncInProgress] = useState(false);

  // ---------- Safe localStorage access ----------
  const getStorage = (): Storage | null => {
    try {
      if (typeof window === 'undefined') return null;
      return window.localStorage;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingActions();
    };

    const handleOffline = () => {
      setIsOnline(false);
      cacheCurrentData();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Load cached data on mount
    loadCachedData();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ---------- Enhanced Caching with Validation ----------
  const cacheCurrentData = () => {
    const storage = getStorage();
    if (!storage) return;

    try {
      // Cache current application state with validation
      const jobs = JSON.parse(storage.getItem('jobs') || '[]');
      const messages = JSON.parse(storage.getItem('messages') || '[]');
      const notifications = JSON.parse(storage.getItem('notifications') || '[]');

      const dataToCache = {
        jobs: Array.isArray(jobs) ? jobs : [],
        messages: Array.isArray(messages) ? messages : [],
        notifications: Array.isArray(notifications) ? notifications : [],
        lastSync: new Date().toISOString(),
        version: '1.0',
        timestamp: Date.now()
      };

      storage.setItem('offline_data', JSON.stringify(dataToCache));
      setOfflineData({
        ...dataToCache,
        lastSync: new Date(dataToCache.lastSync)
      });

      console.log('✅ Current data cached for offline use');
    } catch (error) {
      console.error('❌ Failed to cache current data:', error);
    }
  };

  const loadCachedData = () => {
    const storage = getStorage();
    if (!storage) return;

    try {
      // Load offline data with validation
      const cached = storage.getItem('offline_data');
      if (cached) {
        const data = JSON.parse(cached);
        
        // Version check
        if (data.version === '1.0' && data.timestamp) {
          // Check if cache is not too old (7 days max)
          const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
          if (Date.now() - data.timestamp < maxAge) {
            setOfflineData({
              jobs: Array.isArray(data.jobs) ? data.jobs : [],
              messages: Array.isArray(data.messages) ? data.messages : [],
              notifications: Array.isArray(data.notifications) ? data.notifications : [],
              lastSync: data.lastSync ? new Date(data.lastSync) : null
            });
          } else {
            // Cache too old, remove it
            storage.removeItem('offline_data');
          }
        } else {
          // Invalid version, remove it
          storage.removeItem('offline_data');
        }
      }

      // Load pending actions with validation
      const pending = storage.getItem('pending_actions');
      if (pending) {
        const actions = JSON.parse(pending);
        if (Array.isArray(actions)) {
          // Filter out actions older than 7 days
          const maxAge = 7 * 24 * 60 * 60 * 1000;
          const validActions = actions.filter((action: PendingAction) => {
            const actionAge = Date.now() - new Date(action.timestamp).getTime();
            return actionAge < maxAge;
          });
          
          setPendingActions(validActions);
          
          // Save cleaned actions if any were filtered
          if (validActions.length !== actions.length) {
            storage.setItem('pending_actions', JSON.stringify(validActions));
          }
        }
      }
    } catch (error) {
      console.error('❌ Failed to load cached data:', error);
      // Clear corrupted cache
      storage?.removeItem('offline_data');
      storage?.removeItem('pending_actions');
    }
  };

  // ---------- Enhanced Pending Actions Management ----------
  const addPendingAction = (action: Omit<PendingAction, 'id' | 'timestamp'>) => {
    const storage = getStorage();
    if (!storage) return;

    try {
      const newAction: PendingAction = {
        ...action,
        id: `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        retryCount: 0,
        maxRetries: action.maxRetries || 3
      };

      const newActions = [...pendingActions, newAction];
      
      // Limit pending actions to prevent unbounded growth (max 50)
      const managedActions = newActions.length > 50 
        ? newActions.slice(-50) // Keep the 50 most recent
        : newActions;
      
      setPendingActions(managedActions);
      storage.setItem('pending_actions', JSON.stringify(managedActions));

      console.log(`📝 Added pending action: ${action.type} (total: ${managedActions.length})`);
    } catch (error) {
      console.error('❌ Failed to add pending action:', error);
    }
  };

  const syncPendingActions = async () => {
    if (pendingActions.length === 0 || syncInProgress) return;

    const storage = getStorage();
    if (!storage) return;

    setSyncInProgress(true);

    try {
      console.log(`🔄 Syncing ${pendingActions.length} pending actions...`);
      const successfulActions: string[] = [];
      let syncedCount = 0;

      // Process pending actions when back online
      for (const action of pendingActions) {
        try {
          await processPendingAction(action);
          successfulActions.push(action.id);
          syncedCount++;
          console.log(`✅ Synced action: ${action.type} (${syncedCount}/${pendingActions.length})`);
        } catch (error) {
          console.error(`❌ Failed to sync action ${action.type}:`, error);
          
          // Increment retry count
          action.retryCount = (action.retryCount || 0) + 1;
          
          // Remove if max retries exceeded
          if (action.retryCount >= (action.maxRetries || 3)) {
            successfulActions.push(action.id);
            console.warn(`⚠️ Max retries exceeded for action: ${action.type}`);
          }
        }
      }

      // Remove successfully processed actions
      const remainingActions = pendingActions.filter(action => !successfulActions.includes(action.id));
      setPendingActions(remainingActions);
      storage.setItem('pending_actions', JSON.stringify(remainingActions));
      
      // Update last sync time
      const updatedData = { ...offlineData, lastSync: new Date() };
      setOfflineData(updatedData);
      
      const cacheData = {
        ...updatedData,
        lastSync: updatedData.lastSync?.toISOString(),
        version: '1.0',
        timestamp: Date.now()
      };
      storage.setItem('offline_data', JSON.stringify(cacheData));

      if (syncedCount > 0) {
        console.log(`✅ Sync completed: ${syncedCount} actions synced, ${remainingActions.length} remaining`);
      }

    } catch (error) {
      console.error('❌ Failed to sync pending actions:', error);
    } finally {
      setSyncInProgress(false);
    }
  };

  const processPendingAction = async (action: PendingAction): Promise<void> => {
    if (!action || !action.type || !action.data) {
      throw new Error('Invalid action structure');
    }

    // Add timeout to prevent hanging
    const timeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Action processing timeout')), 10000)
    );

    const processAction = async () => {
      // Process different types of offline actions
      switch (action.type) {
        case 'send_message':
          console.log('💬 Syncing message:', action.data.messageId);
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 500));
          break;
        case 'submit_quote':
          console.log('💰 Syncing quote:', action.data.quoteId);
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000));
          break;
        case 'update_profile':
          console.log('👤 Syncing profile update:', action.data.userId);
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 800));
          break;
        default:
          throw new Error(`Unknown action type: ${action.type}`);
      }
    };

    // Race between processing and timeout
    await Promise.race([processAction(), timeout]);
  };

  // ---------- Utility Functions ----------
  const getOfflineMessage = (type: 'send' | 'submit' | 'update'): string => {
    const messages = {
      send: 'Message will be sent when you\'re back online',
      submit: 'Quote will be submitted when you\'re back online',
      update: 'Changes will be saved when you\'re back online'
    };
    return messages[type] || 'Action will be completed when you\'re back online';
  };

  const clearOfflineData = () => {
    const storage = getStorage();
    if (!storage) return;

    try {
      storage.removeItem('offline_data');
      storage.removeItem('pending_actions');
      
      setOfflineData({
        jobs: [],
        messages: [],
        notifications: [],
        lastSync: null
      });
      setPendingActions([]);
      
      console.log('🗑️ Offline data cleared');
    } catch (error) {
      console.error('❌ Failed to clear offline data:', error);
    }
  };

  const getOfflineStatus = () => ({
    isOnline,
    pendingActionsCount: pendingActions.length,
    syncInProgress,
    lastSync: offlineData.lastSync,
    hasOfflineData: offlineData.jobs.length > 0 || offlineData.messages.length > 0 || offlineData.notifications.length > 0
  });

  return {
    isOnline,
    offlineData,
    pendingActions: pendingActions.length,
    syncInProgress,
    addPendingAction,
    getOfflineMessage,
    clearOfflineData,
    getOfflineStatus,
    lastSync: offlineData.lastSync
  };
}